import { TOPOLOGY_NODE_ICON_URLS } from "./topology-node-icons";

const PREVIEW_ICON_URLS: Record<string, string> = TOPOLOGY_NODE_ICON_URLS;

const PREVIEW_THEME_VARS: Record<string, string> = {
  "--color-surface": "#ffffff",
  "--color-surface-alt": "#f9fafb",
  "--color-border": "#e5e7eb",
  "--color-text-subtle": "#4b5563",
  "--graph-edge-default": "#9ca3af",
  "--graph-node-label": "#374151",
  "--color-text": "#111827",
  "--color-primary": "#2563eb",
};

function resolveAssetRef(value: string, assetApiBaseUrl?: string) {
  const matched = value.trim().match(/^asset:\/\/([^/?#]+)$/i);
  if (!matched?.[1]) return value;
  const normalizedBaseUrl =
    typeof assetApiBaseUrl === "string" ? assetApiBaseUrl.trim().replace(/\/+$/, "") : "";
  if (!normalizedBaseUrl) return value;
  return `${normalizedBaseUrl}/api/assets/${encodeURIComponent(decodeURIComponent(matched[1]))}/download`;
}

function normalizePreviewIconDataUrl(value: string) {
  if (!value.startsWith("data:image/svg+xml;utf8,")) return value;
  const content = value.slice("data:image/svg+xml;utf8,".length);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(content.replace(/%23/g, "#"))}`;
}

function resolvePreviewIconUrl(icon: unknown, assetApiBaseUrl?: string) {
  if (typeof icon !== "string") return "";
  const normalized = icon.trim();
  if (!normalized) return "";
  if (normalized.startsWith("asset://")) return resolveAssetRef(normalized, assetApiBaseUrl);
  if (
    normalized.startsWith("data:") ||
    normalized.startsWith("http://") ||
    normalized.startsWith("https://") ||
    normalized.startsWith("blob:")
  ) {
    return normalizePreviewIconDataUrl(normalized);
  }
  return normalizePreviewIconDataUrl(PREVIEW_ICON_URLS[normalized] || "");
}

function normalizePreviewCssVar(value: string) {
  return value.replace(
    /var\((--[A-Za-z0-9-_]+)\)/g,
    (_matched, token: string) => PREVIEW_THEME_VARS[token] || token,
  );
}

function sanitizeGraphJsonValue(value: unknown): unknown {
  if (typeof value === "string") {
    const resolvedAsset = value.startsWith("asset://") ? resolveAssetRef(value, undefined) : value;
    return normalizePreviewCssVar(resolvedAsset);
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeGraphJsonValue(item));
  }
  if (!value || typeof value !== "object") return value;
  const next: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    next[key] = sanitizeGraphJsonValue(entry);
  }
  return next;
}

function sanitizePreviewRecord<T extends Record<string, unknown>>(value: T): T {
  return sanitizeGraphJsonValue(value) as T;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function asStringArray(values: string[] | undefined) {
  return Array.isArray(values)
    ? values.filter((item) => typeof item === "string" && item.trim()).map((item) => item.trim())
    : [];
}

function readTopologyNodeRuntimeType(data: Record<string, unknown>) {
  return typeof data.runtime_type === "string" && data.runtime_type.trim()
    ? data.runtime_type.trim()
    : typeof data.runtimeType === "string" && data.runtimeType.trim()
      ? data.runtimeType.trim()
      : "";
}

function readTopologyNodeImplementation(data: Record<string, unknown>) {
  return typeof data.implementation === "string" && data.implementation.trim()
    ? data.implementation.trim()
    : typeof data.model === "string" && data.model.trim()
      ? data.model.trim()
      : "";
}

function readTopologyNodeRole(data: Record<string, unknown>) {
  return typeof data.role === "string" && data.role.trim()
    ? data.role.trim()
    : typeof data.functionRole === "string" && data.functionRole.trim()
      ? data.functionRole.trim()
      : typeof data.type === "string" && data.type.trim() === "router"
        ? "router"
        : "";
}

function readTopologyNodeLabel(data: Record<string, unknown>, fallbackId = "") {
  return typeof data.label === "string" && data.label.trim()
    ? data.label.trim()
    : typeof data.name === "string" && data.name.trim()
      ? data.name.trim()
      : typeof data.node_key === "string" && data.node_key.trim()
        ? data.node_key.trim()
        : fallbackId;
}

function truncate(text: string, max = 20) {
  return text.length > max ? `${text.slice(0, max - 1)}...` : text;
}

function inferPreviewIconKey(data: Record<string, unknown>) {
  const explicitIcon = typeof data.icon === "string" ? data.icon.trim() : "";
  const explicitIconKey = typeof data.icon_key === "string" ? data.icon_key.trim() : "";
  if (explicitIcon || explicitIconKey) return explicitIcon || explicitIconKey;

  const role = readTopologyNodeRole(data);
  const implementation = readTopologyNodeImplementation(data);
  const runtimeType = readTopologyNodeRuntimeType(data);
  const category = typeof data.category === "string" ? data.category.trim() : "";
  const type = typeof data.type === "string" ? data.type.trim() : "";

  if (PREVIEW_ICON_URLS[role]) return role;
  if (role === "endpoint" || role === "pc") return "terminal";
  if (implementation === "logical-switch") return "switch";
  if (implementation === "vyos" || implementation === "frr") return "router";
  if (type === "router") return "router";
  if (type === "network" || category === "network") return "network";
  if (category === "security") return "security";
  if (runtimeType === "container" || runtimeType === "vm")
    return "terminal";
  if (type === "terminal" || type === "endpoint") return "terminal";
  return "";
}

function stripPreviewPorts(cell: Record<string, unknown>) {
  delete cell.ports;
  const data =
    cell.data && typeof cell.data === "object" && !Array.isArray(cell.data)
      ? (cell.data as Record<string, unknown>)
      : null;
  if (data) delete data.ports;
}

function readFiniteNumber(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function readCellPosition(cell: Record<string, unknown>) {
  const position = isRecord(cell.position) ? cell.position : null;
  const positionX = position ? readFiniteNumber(position.x) : null;
  const positionY = position ? readFiniteNumber(position.y) : null;
  if (positionX !== null && positionY !== null) {
    return { x: positionX, y: positionY };
  }
  const x = readFiniteNumber(cell.x);
  const y = readFiniteNumber(cell.y);
  return x !== null && y !== null ? { x, y } : null;
}

function readCellSizeDimension(
  cell: Record<string, unknown>,
  key: "width" | "height",
  fallback: number,
) {
  const size = asRecord(cell.size);
  return readFiniteNumber(size[key]) ?? readFiniteNumber(cell[key]) ?? fallback;
}

function writeCellPosition(cell: Record<string, unknown>, x: number, y: number) {
  cell.position = { x, y };
  if (readFiniteNumber(cell.x) !== null || readFiniteNumber(cell.y) !== null) {
    cell.x = x;
    cell.y = y;
  }
}

function isEdgeCell(cell: Record<string, unknown>) {
  return cell.shape === "edge" || Boolean(cell.source && cell.target);
}

function readCellId(cell: Record<string, unknown>) {
  return typeof cell.id === "string" && cell.id.trim() ? cell.id.trim() : "";
}

function isRuntimeCanvasVisible(cell: Record<string, unknown>) {
  const data = asRecord(cell.data);
  const presentation = asRecord(data.presentation);
  return typeof presentation.runtimeCanvasVisible === "boolean"
    ? presentation.runtimeCanvasVisible
    : true;
}

function readEdgeEndpointCellId(endpoint: unknown) {
  if (typeof endpoint === "string" && endpoint.trim()) return endpoint.trim();
  if (!isRecord(endpoint)) return "";
  return typeof endpoint.cell === "string" && endpoint.cell.trim() ? endpoint.cell.trim() : "";
}

function filterRuntimeCanvasHiddenCells(cells: Record<string, unknown>[]) {
  const hiddenCellIds = new Set(
    cells
      .filter((cell) => !isRuntimeCanvasVisible(cell))
      .map(readCellId)
      .filter(Boolean),
  );

  if (hiddenCellIds.size === 0) {
    return cells.filter(isRuntimeCanvasVisible);
  }

  return cells.filter((cell) => {
    if (!isRuntimeCanvasVisible(cell)) return false;
    if (!isEdgeCell(cell)) return true;
    const sourceCellId = readEdgeEndpointCellId(cell.source);
    const targetCellId = readEdgeEndpointCellId(cell.target);
    return !hiddenCellIds.has(sourceCellId) && !hiddenCellIds.has(targetCellId);
  });
}

function readPreviewLayoutLayer(cell: Record<string, unknown>) {
  const data = asRecord(cell.data);
  const type = typeof data.type === "string" ? data.type.trim() : "";
  const category = typeof data.category === "string" ? data.category.trim() : "";
  const role = readTopologyNodeRole(data);
  const implementation = readTopologyNodeImplementation(data);
  const runtimeType = readTopologyNodeRuntimeType(data);

  if (type === "network" || category === "network" || implementation === "logical-switch") return 0;
  if (
    role === "router" ||
    role === "firewall" ||
    role === "security" ||
    implementation === "vyos" ||
    implementation === "frr"
  )
    return 1;
  if (
    runtimeType === "container" ||
    runtimeType === "vm" ||
    type === "terminal" ||
    type === "endpoint"
  )
    return 2;
  return 2;
}

function applyMissingPreviewLayout(cells: Record<string, unknown>[]) {
  const nodeCells = cells.filter((cell) => !isEdgeCell(cell));
  const missingPositionCells = nodeCells.filter((cell) => !readCellPosition(cell));
  if (missingPositionCells.length === 0) return;

  const layers = new Map<number, Record<string, unknown>[]>();
  missingPositionCells.forEach((cell) => {
    const layer = readPreviewLayoutLayer(cell);
    layers.set(layer, [...(layers.get(layer) || []), cell]);
  });

  const sortedLayers = [...layers.keys()].sort((left, right) => left - right);
  const maxLayerWidth = Math.max(1, ...[...layers.values()].map((items) => items.length));
  const spacingX = 220;
  const spacingY = 140;
  const startX = 48;
  const startY = 48;
  const canvasWidth = Math.max(420, startX * 2 + Math.max(1, maxLayerWidth - 1) * spacingX);

  sortedLayers.forEach((layer, layerIndex) => {
    const items = layers.get(layer) || [];
    const rowWidth = Math.max(0, (items.length - 1) * spacingX);
    const xOffset = (canvasWidth - rowWidth) / 2;
    items.forEach((cell, index) => {
      writeCellPosition(
        cell,
        Math.round(xOffset + index * spacingX),
        startY + layerIndex * spacingY,
      );
    });
  });
}

function normalizeTopologyNodeCell(cell: Record<string, unknown>) {
  const data = asRecord(cell.data);
  const runtimeType = readTopologyNodeRuntimeType(data);
  const implementation = readTopologyNodeImplementation(data);
  const role = readTopologyNodeRole(data);
  const label = readTopologyNodeLabel(data, typeof cell.id === "string" ? cell.id.trim() : "");

  cell.shape = "topology-node";
  cell.data = {
    ...data,
    label,
    runtime_type: runtimeType || role || (typeof data.type === "string" ? data.type : ""),
    implementation,
    role,
  };

  if (typeof cell.width === "number" || typeof cell.height === "number") {
    cell.size = {
      width: typeof cell.width === "number" ? cell.width : 180,
      height: typeof cell.height === "number" ? cell.height : 72,
    };
  }

  stripPreviewPorts(cell);
}

function normalizeEdgePreviewCell(cell: Record<string, unknown>) {
  const attrs =
    cell.attrs && typeof cell.attrs === "object" ? (cell.attrs as Record<string, unknown>) : {};
  const line =
    attrs.line && typeof attrs.line === "object" ? (attrs.line as Record<string, unknown>) : {};
  attrs.line = sanitizePreviewRecord({
    ...line,
    targetMarker: null,
    sourceMarker: null,
  });
  cell.attrs = attrs;
}

function normalizeIconPreviewCell(
  cell: Record<string, unknown>,
  options: {
    accessPointNodeKey?: string | undefined;
    isAccessPoint: boolean;
    isEnabledAccessPoint: boolean;
    assetApiBaseUrl?: string | undefined;
  },
) {
  const data = asRecord(cell.data);
  const compactCustomIconNode = cell.shape === "custom-node";
  const attrs =
    cell.attrs && typeof cell.attrs === "object" ? (cell.attrs as Record<string, unknown>) : {};
  const imageAttrs =
    attrs.image && typeof attrs.image === "object" ? (attrs.image as Record<string, unknown>) : {};
  const labelAttrs =
    attrs.label && typeof attrs.label === "object" ? (attrs.label as Record<string, unknown>) : {};
  const labelText = readTopologyNodeLabel(data, typeof cell.id === "string" ? cell.id.trim() : "");
  const compactLabel = truncate(labelText, 14);
  const iconKey = inferPreviewIconKey(data);
  const iconUrl = resolvePreviewIconUrl(
    imageAttrs["xlink:href"] ||
      imageAttrs.xlinkHref ||
      imageAttrs.href ||
      data.icon ||
      data.icon_key ||
      iconKey,
    options.assetApiBaseUrl,
  );

  if (!iconUrl) return false;

  cell.shape = "image";
  cell.data = {
    ...data,
    name: labelText,
    label: labelText,
    ...(typeof data.icon === "string" && data.icon.trim() ? {} : { icon: iconKey }),
    ...(typeof data.icon_key === "string" && data.icon_key.trim() ? {} : { icon_key: iconKey }),
    ...(options.accessPointNodeKey ? { node_key: options.accessPointNodeKey } : {}),
  };
  if (!Array.isArray(cell.markup)) {
    cell.markup = [
      { tagName: "rect", selector: "body" },
      { tagName: "image", selector: "image" },
      { tagName: "text", selector: "label" },
    ];
  }
  attrs.body = {
    refWidth: "100%",
    refHeight: "100%",
    fill:
      attrs.body && typeof attrs.body === "object"
        ? (attrs.body as Record<string, unknown>).fill || "var(--color-surface-alt)"
        : "var(--color-surface-alt)",
    stroke: options.isEnabledAccessPoint
      ? "#2563eb"
      : options.isAccessPoint
        ? "#16a34a"
        : "transparent",
    strokeWidth: options.isAccessPoint ? 3 : 0,
    rx: 12,
    ry: 12,
  };
  attrs.body = sanitizePreviewRecord(attrs.body as Record<string, unknown>);
  attrs.image = sanitizePreviewRecord({
    ...imageAttrs,
    "xlink:href": iconUrl,
    xlinkHref: iconUrl,
    href: iconUrl,
    width: typeof imageAttrs.width === "number" ? imageAttrs.width : 48,
    height: typeof imageAttrs.height === "number" ? imageAttrs.height : 48,
    refWidth: "100%",
    refHeight: "100%",
  });
  attrs.label = sanitizePreviewRecord({
    ...labelAttrs,
    text: compactLabel,
    refY: typeof labelAttrs.refY === "number" ? labelAttrs.refY : 60,
    fill: labelAttrs.fill || "var(--graph-node-label)",
    fontSize: typeof labelAttrs.fontSize === "number" ? labelAttrs.fontSize : 12,
    fontWeight: options.isAccessPoint
      ? 600
      : typeof labelAttrs.fontWeight === "number" || typeof labelAttrs.fontWeight === "string"
        ? labelAttrs.fontWeight
        : 500,
  });
  cell.attrs = attrs;
  const explicitSize = asRecord(cell.size);
  cell.size = {
    width: compactCustomIconNode
      ? readFiniteNumber(explicitSize.width) ?? 48
      : readCellSizeDimension(cell, "width", 48),
    height: compactCustomIconNode
      ? readFiniteNumber(explicitSize.height) ?? 48
      : readCellSizeDimension(cell, "height", 48),
  };
  stripPreviewPorts(cell);
  if (options.isAccessPoint) {
    const width =
      typeof (cell.size as Record<string, unknown>)?.width === "number"
        ? Number((cell.size as Record<string, unknown>).width)
        : 48;
    const badgeColor = options.isEnabledAccessPoint ? "#2563eb" : "#16a34a";
    const badgeLabel = options.isEnabledAccessPoint ? "测试入口" : "接入点";
    cell.markup = [
      { tagName: "rect", selector: "body" },
      { tagName: "image", selector: "image" },
      { tagName: "text", selector: "label" },
      { tagName: "rect", selector: "accessPointBadgeBody" },
      { tagName: "text", selector: "accessPointBadgeLabel" },
    ];
    cell.attrs = {
      ...attrs,
      accessPointBadgeBody: {
        refX: width - 10,
        refY: 2,
        width: options.isEnabledAccessPoint ? 44 : 34,
        height: 18,
        rx: 9,
        ry: 9,
        fill: badgeColor,
        stroke: badgeColor,
        refX2: -1,
      },
      accessPointBadgeLabel: {
        text: badgeLabel,
        refX: width - 10 + (options.isEnabledAccessPoint ? 22 : 17),
        refY: 11,
        textAnchor: "middle",
        textVerticalAnchor: "middle",
        fill: "#ffffff",
        fontSize: 10,
        fontWeight: 700,
      },
    };
  }
  return true;
}

export type ReadonlyTopologyPreviewTransformOptions = {
  accessPointNodeKeys?: string[] | undefined;
  enabledAccessPointNodeKeys?: string[] | undefined;
  assetApiBaseUrl?: string | undefined;
};

export function readTopologyViewSnapshotCells(
  value: Record<string, unknown> | null | undefined,
): Record<string, unknown>[] {
  if (value === null || value === undefined) return [];
  if (value.kind !== "topology_view_snapshot") {
    throw new Error("TOPOLOGY_VIEW_SNAPSHOT_ARTIFACT_CONTRACT_INVALID:kind");
  }
  const artifactPayload = value.payload;
  if (!isRecord(artifactPayload)) {
    throw new Error("TOPOLOGY_VIEW_SNAPSHOT_ARTIFACT_CONTRACT_INVALID:payload");
  }
  if (artifactPayload.schema !== "airange.topology.view.v1") {
    throw new Error("TOPOLOGY_VIEW_SNAPSHOT_ARTIFACT_CONTRACT_INVALID:payload.schema");
  }
  if (artifactPayload.kind !== "topo2.topology.view") {
    throw new Error("TOPOLOGY_VIEW_SNAPSHOT_ARTIFACT_CONTRACT_INVALID:payload.kind");
  }
  const viewPayload = artifactPayload.payload;
  if (!isRecord(viewPayload)) {
    throw new Error("TOPOLOGY_VIEW_SNAPSHOT_ARTIFACT_CONTRACT_INVALID:payload.payload");
  }
  if (!isRecord(viewPayload.meta)) {
    throw new Error("TOPOLOGY_VIEW_SNAPSHOT_ARTIFACT_CONTRACT_INVALID:payload.payload.meta");
  }
  const x6 = viewPayload.x6;
  if (!isRecord(x6)) {
    throw new Error("TOPOLOGY_VIEW_SNAPSHOT_ARTIFACT_CONTRACT_INVALID:payload.payload.x6");
  }
  const cells = x6.cells;
  if (!Array.isArray(cells)) {
    throw new Error("TOPOLOGY_VIEW_SNAPSHOT_ARTIFACT_CONTRACT_INVALID:payload.payload.x6.cells");
  }
  return cells.map((cell, index) => {
    if (!isRecord(cell)) {
      throw new Error(
        `TOPOLOGY_VIEW_SNAPSHOT_ARTIFACT_CONTRACT_INVALID:payload.payload.x6.cells[${index}]`,
      );
    }
    return cell;
  });
}

export function readTopologyViewSnapshotGraphJson(
  value: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  const cells = readTopologyViewSnapshotCells(value);
  return cells.length > 0 ? { cells } : null;
}

export function transformGraphJsonForPreview(
  raw: Record<string, unknown>,
  options?: ReadonlyTopologyPreviewTransformOptions,
) {
  const json = sanitizeGraphJsonValue(raw);
  if (!json || typeof json !== "object") return null;
  const next = json as Record<string, unknown>;
  const accessPointNodeKeys = new Set(asStringArray(options?.accessPointNodeKeys));
  const enabledAccessPointNodeKeys = new Set(asStringArray(options?.enabledAccessPointNodeKeys));
  const transformCell = (cell: Record<string, unknown>) => {
    stripPreviewPorts(cell);
    const data = asRecord(cell.data);
    const nodeKeyCandidates = [
      typeof cell.id === "string" ? cell.id.trim() : "",
      typeof data.node_key === "string" ? data.node_key.trim() : "",
      typeof data.nodeKey === "string" ? data.nodeKey.trim() : "",
      typeof data.key === "string" ? data.key.trim() : "",
    ].filter(Boolean);
    const matchedNodeKey =
      nodeKeyCandidates.find(
        (key) => accessPointNodeKeys.has(key) || enabledAccessPointNodeKeys.has(key),
      ) || "";
    const isAccessPoint = matchedNodeKey ? accessPointNodeKeys.has(matchedNodeKey) : false;
    const isEnabledAccessPoint = matchedNodeKey
      ? enabledAccessPointNodeKeys.has(matchedNodeKey)
      : false;
    if (cell.shape === "edge") {
      normalizeEdgePreviewCell(cell);
      return;
    }
    if (cell.shape === "icon-label") cell.shape = "icon-label-preview";
    if (cell.shape === "custom-node") {
      if (
        !normalizeIconPreviewCell(cell, {
          accessPointNodeKey: matchedNodeKey,
          isAccessPoint,
          isEnabledAccessPoint,
          assetApiBaseUrl: options?.assetApiBaseUrl,
        })
      ) {
        normalizeTopologyNodeCell(cell);
      }
    }
    if (cell.shape === "image") {
      normalizeIconPreviewCell(cell, {
        accessPointNodeKey: matchedNodeKey,
        isAccessPoint,
        isEnabledAccessPoint,
        assetApiBaseUrl: options?.assetApiBaseUrl,
      });
    }
  };
  if (Array.isArray(next.cells)) {
    const cells = filterRuntimeCanvasHiddenCells(next.cells.filter(isRecord));
    next.cells = cells;
    cells.forEach(transformCell);
    applyMissingPreviewLayout(cells);
  } else if (Array.isArray(next.nodes)) {
    const nodes = filterRuntimeCanvasHiddenCells(next.nodes.filter(isRecord));
    next.nodes = nodes;
    nodes.forEach(transformCell);
    applyMissingPreviewLayout(nodes);
  }
  return next;
}

export const __topologyX6PreviewTestables = {
  readTopologyViewSnapshotCells,
  readTopologyViewSnapshotGraphJson,
  transformGraphJsonForPreview,
};
